import React from 'react';
import {
  Dialog, DialogContent, DialogActions,
  Button, TextField, Grid, FormControl, InputLabel, Select, MenuItem,
  Typography, Box, Chip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { SelectChangeEvent } from '@mui/material/Select';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { PRIMARY } from 'themes/colors';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';

interface IConnection {
  totalConnection: number;
  load: number;
  regulator: number;
  pipe: number;
  paidLight: number;
  bplOven: number;
  nonHpOven: number;
  hpOven: number;
  paidAmount: number;
  remarks: string;
}

interface IAgent {
  agent: string;
  totalConnection: number;
  load: number;
  regulator: number;
  pipe: number;
  paidLight: number;
  nonHpOven: number;
  hpOven: number;
  bplOven: number;
  paidAmount: number;
  remarks: string;
}

interface UpdateDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  agent: IAgent;
  connection: IConnection;
  handleChange: {
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void;
    (e: SelectChangeEvent<string>): void;
  };
  handleUpdate: () => void;
}

const UpdateDialog: React.FC<UpdateDialogProps> = ({
  open, setOpen, agent, connection, handleChange, handleUpdate,
}) => {
  const summaryItems = [
    { label: 'Total', value: agent.totalConnection },
    { label: 'Load', value: agent.load },
    { label: 'Regulator', value: agent.regulator },
    { label: 'Pipe', value: agent.pipe },
    { label: 'Light Paid', value: agent.paidLight },
    { label: 'Non HP Oven', value: agent.nonHpOven },
    { label: 'HP Oven', value: agent.hpOven },
    { label: 'BPL Oven', value: agent.bplOven },
    { label: 'Amount Paid', value: agent.paidAmount },
  ];

  const updateFields = [
    { name: 'load', label: 'Load' },
    { name: 'regulator', label: 'Regulator' },
    { name: 'pipe', label: 'Pipe' },
    { name: 'paidLight', label: 'Light Paid' },
    { name: 'bplOven', label: 'BPL Oven' },
    { name: 'nonHpOven', label: 'Non HP Oven' },
    { name: 'hpOven', label: 'HP Oven' },
    { name: 'paidAmount', label: 'Total Amount Paid' },
    { name: 'remarks', label: 'Remarks' },
  ];

  const burnerTypes = ['GT 2B', 'GT 3B', 'GT 4B', 'SINGLE B', 'EXTRA B'];

  return (
    <Dialog
      open={open}
      onClose={() => setOpen(false)}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
    >
      {/* Custom Header */}
      <Box
        sx={{
          px: 3, py: 2.5,
          background: `linear-gradient(135deg, ${PRIMARY.darker}, ${PRIMARY.main})`,
          color: 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <EditNoteIcon />
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>Update Connection</Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              Agent:{' '}
              <span style={{ color: '#ffd740', fontWeight: 600 }}>{agent.agent}</span>
            </Typography>
          </Box>
        </Box>
        <Button
          onClick={() => setOpen(false)}
          sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 'auto', p: 0.5 }}
        >
          <CloseIcon />
        </Button>
      </Box>

      <DialogContent sx={{ p: 0 }}>
        {/* Current Stats Summary */}
        <Box
          sx={{
            p: { xs: 2, sm: 2.5 },
            bgcolor: '#f8fafc',
            borderBottom: '1px solid', borderColor: 'grey.200',
          }}
        >
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 1.5, display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}
          >
            Current Stats
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {summaryItems.map((item) => (
              <Chip
                key={item.label}
                label={`${item.label}: ${item.value}`}
                size="small"
                sx={{
                  bgcolor: 'white',
                  border: '1px solid', borderColor: 'grey.300',
                  fontSize: 12,
                }}
              />
            ))}
          </Box>
        </Box>

        {/* Update Form */}
        <Box sx={{ p: { xs: 2, sm: 3 } }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mb: 2, display: 'block', textTransform: 'uppercase', letterSpacing: 1 }}
          >
            Today's Delivery Update
          </Typography>
          <Grid container spacing={2}>
            {updateFields.map((field) => (
              <Grid item xs={12} sm={6} md={4} key={field.name}>
                <TextField
                  fullWidth
                  size="small"
                  label={field.label}
                  type={field.name === 'remarks' ? 'text' : 'number'}
                  value={connection[field.name as keyof IConnection]}
                  name={field.name}
                  variant="outlined"
                  onChange={handleChange}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            ))}
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>Burner Type</InputLabel>
                <Select
                  onChange={handleChange}
                  name="burnerType"
                  label="Burner Type"
                  sx={{ borderRadius: 2 }}
                >
                  {burnerTypes.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3, py: 2,
          bgcolor: '#f8fafc',
          borderTop: '1px solid', borderColor: 'grey.200',
          gap: 1.5,
        }}
      >
        <Button
          onClick={() => setOpen(false)}
          variant="outlined"
          sx={{
            borderRadius: 2, px: 3,
            borderColor: 'grey.300', color: 'text.secondary',
            '&:hover': { borderColor: 'grey.400', bgcolor: alpha('#000', 0.04) },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleUpdate}
          variant="contained"
          startIcon={<SaveIcon />}
          sx={{
            borderRadius: 2, px: 3,
            background: `linear-gradient(135deg, ${PRIMARY.darker}, ${PRIMARY.main})`,
            boxShadow: '0 4px 12px rgba(13,154,79,0.3)',
            fontWeight: 600,
            '&:hover': { background: `linear-gradient(135deg, ${PRIMARY.dark}, ${PRIMARY.main})` },
          }}
        >
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UpdateDialog;
